import { Check, Loader2, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState, type TouchEvent } from "react";
import type { MomentCategoryNode } from "../../types";
import { CategoryAccordionPicker } from "./CategoryAccordionPicker";

type Props = {
  file: File;
  categories: MomentCategoryNode[];
  saving: boolean;
  onRetake: () => void;
  onSubmit: (input: { categoryId: string | null; note: string }) => void;
  onManage?: () => void;
};

function touchDistance(touches: TouchEvent["touches"]): number {
  const [a, b] = [touches[0], touches[1]];
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

// Post-capture sheet: preview (pinch to inspect), 2-level category picker, optional note, save.
export function MomentReview({ file, categories, saving, onRetake, onSubmit, onManage }: Props) {
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [scale, setScale] = useState(1);
  const pinchRef = useRef<{ startDist: number; startScale: number } | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setScale(1);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onTouchStart = (event: TouchEvent) => {
    if (event.touches.length === 2) {
      pinchRef.current = { startDist: touchDistance(event.touches), startScale: scale };
    }
  };
  const onTouchMove = (event: TouchEvent) => {
    if (event.touches.length === 2 && pinchRef.current) {
      const ratio = touchDistance(event.touches) / pinchRef.current.startDist;
      setScale(Math.min(4, Math.max(1, pinchRef.current.startScale * ratio)));
    }
  };
  const onTouchEnd = () => {
    pinchRef.current = null;
  };

  return (
    <div className="mx-auto flex min-h-[100dvh] w-full max-w-lg flex-col bg-paper">
      <div className="flex items-center justify-between px-4 py-3">
        <button
          type="button"
          onClick={onRetake}
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-white px-3 py-2 text-sm font-semibold text-ink hover:bg-slate-50 disabled:opacity-60"
        >
          <RefreshCw size={16} aria-hidden="true" />
          Chụp lại
        </button>
        <p className="text-sm font-bold text-ink">Phân loại khoảnh khắc</p>
        <span className="w-[92px]" />
      </div>

      <div
        className="mx-4 overflow-hidden rounded-xl bg-black"
        style={{ touchAction: "none" }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onDoubleClick={() => setScale(1)}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Ảnh vừa chụp"
            className="max-h-[46dvh] w-full object-contain transition-transform"
            style={{ transform: `scale(${scale})` }}
          />
        ) : null}
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
        <CategoryAccordionPicker
          categories={categories}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onManage={onManage}
        />

        <div>
          <label htmlFor="moment-note" className="mb-1 block text-sm font-semibold text-ink">
            Ghi chú (tuỳ chọn)
          </label>
          <textarea
            id="moment-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={2}
            placeholder="Bạn đang làm gì, cảm thấy thế nào…"
            className="w-full rounded-lg border border-line bg-slate-50 p-3 text-sm outline-none focus:border-blue-300 focus:bg-white"
          />
        </div>
      </div>

      <div
        className="border-t border-line bg-white px-4 py-3"
        style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
      >
        <button
          type="button"
          disabled={saving}
          onClick={() => onSubmit({ categoryId: selectedId, note: note.trim() })}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 py-3 text-sm font-bold text-white hover:bg-blue-700 disabled:bg-slate-300"
        >
          {saving ? (
            <>
              <Loader2 size={18} className="animate-spin" aria-hidden="true" />
              Đang lưu…
            </>
          ) : (
            <>
              <Check size={18} aria-hidden="true" />
              Lưu khoảnh khắc
            </>
          )}
        </button>
      </div>
    </div>
  );
}
